import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Position,
  Handle,
  MarkerType,
  NodeToolbar,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "@dagrejs/dagre";

const Graph = dagre.graphlib?.Graph || dagre.Graph;
import * as LucideIcons from "lucide-react";
const { RefreshCw, AlertCircle, Network, Zap, Search, ExternalLink, PenLine, Wand2, Link2, GitMerge, Loader2, Plus, Check, Share2, Sparkles, X, Lightbulb, Target, StickyNote, FileText } = LucideIcons;
import { Link, useNavigate } from "react-router-dom";
import {
  getQueryTopics, getPerformanceQueries, getFanoutQueries, getClusters, generateOutline, getOutlineStatus,
  fanoutBridge, generateContentStrategy,
} from "../lib/api";
import { useOrgId } from "../hooks/useOrgId";
import { MOVES, TIERS, topicMove } from "../lib/queryMapPlan";
import { usePersistentState } from "../components/search-performance/shared";

// ── Node dimensions ──────────────────────────────────────────────────────────

// Cards grow with their rows (ContextMinds-style: a heading plus the questions under it),
// so dagre needs each node's real height, not one fixed NODE_H.
const CARD_W = 260;
const HEAD_H = 46;
const MOVE_H = 20;
const ROW_H = 24;
const FOOT_H = 20;
const PLAIN_W = 200;
const PLAIN_H = 52;
const MAX_ROWS = 6;

const TYPE_CONFIG = {
  root:    { color: "#018D4C", bg: "#E6F5EE", border: "#018D4C" },
  topic:   { color: "#9A3412", bg: "#FFEDD5", border: "#FB923C" },
  fanout:  { color: "#0369A1", bg: "#E0F2FE", border: "#38BDF8" },
  cluster: { color: "#F9FAFB", bg: "#1F2937", border: "#374151" },
  page:    { color: "#5B21B6", bg: "#F3E8FF", border: "#C4B5FD" },
  bridge:  { color: "#6D28D9", bg: "#FAF5FF", border: "#A78BFA" },
  idea:    { color: "#854D0E", bg: "#FEFCE8", border: "#FACC15" },
  note:    { color: "#713F12", bg: "#FEF08A", border: "#EAB308" },
  strategy:      { color: "#0F766E", bg: "#CCFBF1", border: "#14B8A6" },
  strategyCluster:{ color: "#115E59", bg: "#F0FDFA", border: "#2DD4BF" },
};
const BRIDGE_NOTE_H = 30;
const VIOLET = "#8B5CF6";

const GREEN = "#10B981";
const AMBER = "#F59E0B";
const RED = "#F87171";
const GREY = "#9CA3AF";

const INTENT_COLORS = {
  informational: "#3B82F6",
  commercial: "#F59E0B",
  transactional: "#10B981",
  comparison: "#8B5CF6",
  problem_aware: "#EF4444",
};
function intentColor(intent) { return INTENT_COLORS[intent] || GREY; }

const ACTIONS = {
  write: { label: "Write article", icon: PenLine },
  copy:  { label: "Rewrite page copy", icon: Wand2 },
  links: { label: "Find link spots", icon: Link2 },
  merge: { label: "Review overlap", icon: GitMerge },
};

// Outline brief: the topic plus every search it must answer, real (GSC gaps) and synthetic
// (fan-out gaps), so the article covers the sub-questions the map shows as red rows.
function outlineReason(data) {
  const must = [...new Set([...data.gapQueries, ...data.fanoutGaps])].slice(0, 15);
  return [
    data.problem,
    `Search topic with ${data.impressions || 0} impressions across ${data.queryCount} searches and no page that answers it.`,
    must.length ? `Answer each of these searches (Search Console + AI fan-out):\n${must.map((q) => `- ${q}`).join("\n")}` : "",
  ].filter(Boolean).join("\n\n");
}

function bridgeReason(data) {
  return [
    `Missing link between "${data.between[0]}" and "${data.between[1]}": ${data.concept}.`,
    data.focus ? `Write it for this audience and situation: ${data.focus}. Use examples from it throughout.` : "",
    data.why,
    `Answer these searches:\n${[...data.rows.map((r) => r.text), ...data.serves].map((q) => `- ${q}`).join("\n")}`,
    `Link to and from the pages for "${data.between[0]}" and "${data.between[1]}".`,
  ].filter(Boolean).join("\n\n");
}

async function waitForOutline(id) {
  // Queued time doesn't count against the budget, like Recommendations' pollOutlineReady.
  let active = 0;
  for (let i = 0; i < 450 && active < 90; i++) {
    await new Promise((r) => setTimeout(r, 2000));
    const { status, error } = await getOutlineStatus(id);
    if (status === "done" || status === "needs_revision") return;
    if (status === "error") throw new Error(error || "Outline generation failed");
    if (status !== "queued") active += 1;
  }
  throw new Error("Outline generation timed out");
}

function cfg(type) {
  return TYPE_CONFIG[type] || TYPE_CONFIG.topic;
}

function nodeSize(n) {
  if (["topic", "fanout", "bridge", "idea"].includes(n.data.kind)) {
    const rows = Math.min(n.data.rows.length, MAX_ROWS);
    const more = n.data.rows.length > MAX_ROWS || n.data.kind === "topic" ? FOOT_H : 0;
    const head = HEAD_H + (n.data.kind === "topic" ? MOVE_H : 0) + (n.data.kind === "bridge" ? BRIDGE_NOTE_H : 0);
    return { w: CARD_W, h: head + Math.max(rows, 1) * ROW_H + more + 8 };
  }
  return { w: PLAIN_W, h: PLAIN_H };
}

function fmt(n) {
  if (n == null) return "–";
  if (n >= 10000) return `${Math.round(n / 1000)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function key(s) {
  return (s || "").trim().toLowerCase();
}

// ── Custom nodes ──────────────────────────────────────────────────────────────

function CardNode({ data, selected }) {
  const { color, bg, border } = cfg(data.kind);
  const rows = data.rows.slice(0, MAX_ROWS);
  const hidden = data.rows.length - rows.length;
  const navigate = useNavigate();
  const move = MOVES[data.move];
  const action = move && ACTIONS[move.action];
  const [busy, setBusy] = useState(null);

  const write = async (reason, cluster) => {
    setBusy("Outlining…");
    try {
      const o = await generateOutline({ title: data.label, cluster, reason });
      if (o.status === "generating" || o.status === "queued") await waitForOutline(o.id);
      navigate(`/dashboard/writing/${o.id}`);
    } catch (e) {
      setBusy(null);
      alert("Outline failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const act = async () => {
    if (move.action === "write") return write(outlineReason(data), "Search topic");
    // Copy and link work happen on the topic's top query: Query Detail crawls the ranking page
    // and returns a new H1, intro, missing sections, title/meta and internal link spots.
    if (move.action === "merge") navigate("/dashboard/cannibalization");
    else if (data.actionQueryId) navigate(`/dashboard/query-gaps/${data.actionQueryId}`);
  };

  return (
    <>
      <NodeToolbar isVisible={selected} position={Position.Top} style={{ padding: 0 }}>
        <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-md px-2 py-1 shadow text-xs">
          {data.kind === "bridge" && !data.pending && !data.coveredBy && (
            <button
              onClick={() => write(bridgeReason(data), "Missing link")}
              disabled={!!busy}
              title="Generate an outline for this bridge page"
              className="flex items-center gap-1 font-semibold text-violet-700 disabled:opacity-60"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <PenLine size={12} />}
              {busy || "Write article"}
            </button>
          )}
          {data.kind === "idea" && (
            <button onClick={() => data.onRemoveIdea(data.label)} className="flex items-center gap-1 text-gray-500 hover:text-gray-800">
              <X size={12} /> Remove idea
            </button>
          )}
          {data.kind === "bridge" && !data.pending && (
            <button onClick={() => data.onDismiss(data.bridgeId)} className="flex items-center gap-1 text-gray-500 hover:text-gray-800">
              <X size={12} /> Dismiss
            </button>
          )}
          {action && (move.action === "write" || move.action === "merge" || data.actionQueryId) && (
            <button
              onClick={act}
              disabled={!!busy}
              title={move.action === "write" ? "Generate an outline that answers this topic's searches and fan-out gaps" : undefined}
              className="flex items-center gap-1 font-semibold disabled:opacity-60"
              style={{ color: move.color }}
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <action.icon size={12} />}
              {busy || action.label}
            </button>
          )}
          {data.kind === "topic" && data.relatedOff.length > 0 && data.onAddRelated && (
            <button
              onClick={() => data.onAddRelated(data.relatedOff)}
              title={`Related by shared searches or ranking page: ${data.relatedOff.join(", ")}`}
              className="flex items-center gap-1 font-medium text-violet-700 hover:text-violet-900"
            >
              <Share2 size={12} /> Add related ({data.relatedOff.length})
            </button>
          )}
          {data.kind === "topic" && (
            <button
              onClick={() => navigate(`/dashboard/fanout?seed=${encodeURIComponent(data.label)}`)}
              className="flex items-center gap-1 font-medium text-emerald-700 hover:text-emerald-900"
            >
              <Zap size={12} /> {data.hasFanout ? "Re-run fan-out" : "Run fan-out"}
            </button>
          )}
          {data.kind === "fanout" && (
            <button
              onClick={() => navigate(`/dashboard/fanout?seed=${encodeURIComponent(data.label)}&history=1`)}
              className="flex items-center gap-1 font-medium text-sky-700 hover:text-sky-900"
            >
              <ExternalLink size={12} /> Open fan-out
            </button>
          )}
          {data.kind === "strategy" && data.onRemove && (
            <button onClick={() => data.onRemove(data.strategyId)} className="flex items-center gap-1 text-gray-500 hover:text-gray-800">
              <X size={12} /> Remove strategy
            </button>
          )}
          {data.kind === "strategyCluster" && (
            <button
              onClick={() => write(data.reason, data.clusterLabel)}
              disabled={!!busy}
              title="Generate an outline that answers this cluster's searches"
              className="flex items-center gap-1 font-semibold text-teal-700 disabled:opacity-60"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <PenLine size={12} />}
              {busy || "Write article"}
            </button>
          )}
        </div>
      </NodeToolbar>
      <div
        title={data.label}
        style={{
          width: CARD_W,
          height: data.h,
          background: bg,
          border: `1.5px ${data.kind === "bridge" ? "dashed" : "solid"} ${border}`,
          borderRadius: 10,
          opacity: data.coveredBy ? 0.75 : 1,
          boxShadow: selected ? `0 0 0 3px ${border}55` : "0 1px 3px rgba(0,0,0,0.08)",
          overflow: "hidden",
          fontFamily: "inherit",
        }}
      >
        <Handle type="target" position={Position.Left} style={{ background: border }} />
        <div style={{ padding: "8px 10px 6px", borderBottom: `1px solid ${border}66` }}>
          <div
            style={{
              fontSize: 12, fontWeight: 700, color, lineHeight: 1.25,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            {data.kind === "fanout" && <Zap size={10} style={{ display: "inline", marginRight: 4 }} />}
            {data.kind === "idea" && <Lightbulb size={10} style={{ display: "inline", marginRight: 4 }} />}
            {data.kind === "bridge" && (data.pending
              ? <Loader2 size={10} className="animate-spin" style={{ display: "inline", marginRight: 4 }} />
              : <Sparkles size={10} style={{ display: "inline", marginRight: 4 }} />)}
            {data.kind === "strategy" && <Target size={10} style={{ display: "inline", marginRight: 4 }} />}
            {data.kind === "strategyCluster" && <Network size={10} style={{ display: "inline", marginRight: 4 }} />}
            {data.label}
          </div>
          <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>{data.sub}</div>
          {data.kind === "bridge" && (
            <div
              title={data.why}
              style={{
                fontSize: 10, color: data.coveredBy ? "#047857" : "#4C1D95", marginTop: 3, lineHeight: "13px", height: 26,
                overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
              }}
            >
              {data.coveredBy ? `Already covered by “${data.coveredBy}”` : data.why}
            </div>
          )}
          {data.tier && TIERS[data.tier] && (
            <div
              title={data.tierWhy || "Lead potential: buyer fit and awareness, not search volume"}
              style={{
                display: "inline-block", marginTop: 4, marginRight: 4, padding: "1px 7px", borderRadius: 9, fontSize: 9.5,
                fontWeight: 700, lineHeight: "14px", background: TIERS[data.tier].bg, color: TIERS[data.tier].text,
                border: "1px solid #E4E4E7",
              }}
            >
              {TIERS[data.tier].label}
            </div>
          )}
          {move && (
            <div
              title={data.moveGuessed ? "Not judged yet: inferred from coverage" : `Next move, ${Math.round((data.moveConfidence || 0) * 100)}% confident`}
              style={{
                display: "inline-block", marginTop: 4, padding: "1px 7px", borderRadius: 9, fontSize: 9.5, fontWeight: 700,
                color: "white", background: move.color, opacity: data.moveGuessed ? 0.7 : 1, lineHeight: "14px",
              }}
            >
              → {move.label}{data.moveGuessed ? "?" : ""}
            </div>
          )}
        </div>
        <div style={{ padding: "4px 6px" }}>
          {rows.length === 0 && (
            <div style={{ fontSize: 11, color: GREY, padding: "4px 4px", height: ROW_H }}>
              {data.kind === "idea" ? "Not searched yet · drag onto a topic" : "No queries stored"}
            </div>
          )}
          {rows.map((r, i) => (
            <div
              key={i}
              title={r.text}
              style={{
                opacity: data.rowFilter && !data.rowFilter.includes(r.dot) ? 0.25 : 1,
                display: "flex", alignItems: "center", gap: 6, height: ROW_H - 2, marginBottom: 2,
                padding: "0 6px", background: "white", borderRadius: 5, fontSize: 11, color: "#1F2937",
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: 3, background: r.dot, flexShrink: 0 }} />
              <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.text}</span>
              {r.meta && <span style={{ fontSize: 10, color: "#6B7280", flexShrink: 0 }}>{r.meta}</span>}
            </div>
          ))}
          {(hidden > 0 || data.kind === "topic") && (
            <div style={{ fontSize: 10, color: "#6B7280", padding: "0 6px", height: FOOT_H, lineHeight: `${FOOT_H}px` }}>
              {hidden > 0 ? `+${hidden} more` : ""}
              {data.kind === "topic" && data.queryCount > data.rows.length
                ? `${hidden > 0 ? " · " : ""}${data.queryCount} queries in topic`
                : ""}
            </div>
          )}
        </div>
        <Handle type="source" position={Position.Right} style={{ background: border }} />
      </div>
    </>
  );
}

function PlainNode({ data, selected }) {
  const { color, bg, border } = cfg(data.kind);
  const navigate = useNavigate();
  return (
    <>
    {data.kind === "cluster" && data.clusterId && (
      <NodeToolbar isVisible={selected} position={Position.Top} style={{ padding: 0 }}>
        <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-md px-2 py-1 shadow text-xs">
          <button
            onClick={() => navigate(`/dashboard/articles?cluster=${encodeURIComponent(data.clusterId)}`)}
            className="flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900"
          >
            <FileText size={12} /> Open {data.size ?? ""} articles
          </button>
        </div>
      </NodeToolbar>
    )}
    <div
      title={data.url || data.label}
      style={{
        width: PLAIN_W, height: PLAIN_H, background: bg, border: `1.5px solid ${border}`, borderRadius: 10,
        display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 12px",
        boxShadow: selected ? `0 0 0 3px ${border}55` : "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: border }} />
      <div
        style={{
          fontSize: 12, fontWeight: 600, color, lineHeight: 1.25,
          overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        }}
      >
        {data.label}
      </div>
      {data.sub && <div style={{ fontSize: 10, color: data.kind === "cluster" ? "#9CA3AF" : "#6B7280" }}>{data.sub}</div>}
      <Handle type="source" position={Position.Right} style={{ background: border }} />
    </div>
    </>
  );
}

// Sticky note: the owner's free-text reminder, dropped anywhere on the canvas. Not part of
// the graph (no handles, no layout): it keeps the spot it was dragged to.
const NOTE_W = 200;
const NOTE_H = 140;

function NoteNode({ data, selected }) {
  const { color, bg, border } = cfg("note");
  return (
    <div
      style={{
        width: NOTE_W, height: NOTE_H, background: bg, border: `1px solid ${border}`, borderRadius: 4,
        boxShadow: selected ? `0 0 0 3px ${border}55, 0 4px 10px rgba(0,0,0,0.12)` : "0 4px 10px rgba(0,0,0,0.12)",
        display: "flex", flexDirection: "column", transform: "rotate(-1deg)",
      }}
    >
      <div className="flex items-center justify-between px-2 pt-1 cursor-move" style={{ fontSize: 10, color, fontWeight: 700 }}>
        <span className="flex items-center gap-1"><StickyNote size={10} /> Note</span>
        <button
          onClick={() => data.onDelete(data.noteId)}
          className="nodrag opacity-50 hover:opacity-100"
          title="Delete note"
        >
          <X size={12} />
        </button>
      </div>
      <textarea
        className="nodrag nowheel flex-1 w-full resize-none bg-transparent px-2 pb-2 pt-1 focus:outline-none"
        style={{ fontSize: 12, color: "#422006", lineHeight: 1.35 }}
        defaultValue={data.text}
        autoFocus={data.autoFocus}
        placeholder="Write a note…"
        onBlur={(e) => e.target.value !== data.text && data.onChange(data.noteId, { text: e.target.value })}
      />
    </div>
  );
}

const NODE_TYPES = { card: CardNode, plain: PlainNode, note: NoteNode };

// ── Layout ───────────────────────────────────────────────────────────────────

// One dagre pass over everything stacks every topic in a single column (they all share a
// rank). Instead each connected group (topic → fan-out → cluster/page) gets its own dagre
// pass, and the groups are packed into rows, the way a hand-arranged ContextMinds map reads.
const ROW_MAX_W = 2200;
const GROUP_GAP = 70;

function layoutGroup(nodes, edges) {
  const g = new Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 24, ranksep: 70 });
  nodes.forEach((n) => {
    const { w, h } = nodeSize(n);
    g.setNode(n.id, { width: w, height: h });
  });
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  let maxX = 0;
  let maxY = 0;
  const placed = nodes.map((n) => {
    const pos = g.node(n.id);
    const { w, h } = nodeSize(n);
    const x = pos.x - w / 2;
    const y = pos.y - h / 2;
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
    return { ...n, data: { ...n.data, h }, position: { x, y } };
  });
  return { placed, w: maxX, h: maxY };
}

function packLayout(nodes, edges) {
  const parent = new Map(nodes.map((n) => [n.id, n.id]));
  const find = (id) => (parent.get(id) === id ? id : find(parent.get(id)));
  edges.forEach((e) => parent.set(find(e.source), find(e.target)));
  const groups = new Map();
  nodes.forEach((n) => {
    const r = find(n.id);
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r).push(n);
  });

  const laid = [...groups.values()]
    .map((gn) => {
      const ids = new Set(gn.map((n) => n.id));
      return layoutGroup(gn, edges.filter((e) => ids.has(e.source)));
    })
    .sort((a, b) => b.w * b.h - a.w * a.h);

  const out = [];
  let x = 0;
  let y = 0;
  let rowH = 0;
  laid.forEach(({ placed, w, h }) => {
    if (x > 0 && x + w > ROW_MAX_W) {
      x = 0;
      y += rowH + GROUP_GAP;
      rowH = 0;
    }
    placed.forEach((n) => out.push({ ...n, position: { x: n.position.x + x, y: n.position.y + y } }));
    x += w + GROUP_GAP;
    rowH = Math.max(rowH, h);
  });
  return out;
}

function edge(source, target, color, extra = {}) {
  return {
    id: `${source}->${target}`,
    source,
    target,
    style: { stroke: color, strokeWidth: 1.5, ...(extra.dashed ? { strokeDasharray: "5 4" } : {}) },
    markerEnd: { type: MarkerType.ArrowClosed, color },
  };
}

// ── Data indexes ──────────────────────────────────────────────────────────────

// ── Relations (Search Console only) ─────────────────────────────────────────

// Same tokenizer as the backend's Related Queries (internal_link_service._keywords).
const STOPWORDS = new Set([
  "the", "a", "an", "of", "for", "and", "or", "to", "in", "on", "with", "is",
  "are", "how", "what", "why", "your", "you", "this", "that", "best",
]);
const words = (text) => (text || "").toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((w) => w.length >= 3 && !STOPWORDS.has(w));

// A word in this share of topics or more (the brand, the product category) relates everything
// to everything, so it can't say two topics are related.
const COMMON_SHARE = 0.3;
const RELATED_PER_TOPIC = 5;
const PAGES_PER_TOPIC = 2;

function normUrl(u) {
  return (u || "").replace(/^https?:\/\/(www\.)?/, "").replace(/[?#].*$/, "").replace(/\/$/, "");
}

// Two GSC signals relate topics: their searches share distinctive words, or Google ranks the
// same page for both. Each topic keeps its top related topics, strongest first.
function relateTopics(topics, byTopic) {
  const terms = new Map();
  const pages = new Map();
  topics.forEach((t) => {
    const qs = byTopic.get(t.id) || [];
    terms.set(t.id, new Set([t.topic, ...qs.map((q) => q.query)].flatMap(words)));
    pages.set(t.id, new Set(qs.map((q) => normUrl(q.url)).filter(Boolean)));
  });
  const df = new Map();
  terms.forEach((ws) => ws.forEach((w) => df.set(w, (df.get(w) || 0) + 1)));
  const tooCommon = (w) => df.get(w) >= Math.max(3, COMMON_SHARE * topics.length);

  const related = new Map(topics.map((t) => [t.id, []]));
  for (let i = 0; i < topics.length; i++) {
    for (let j = i + 1; j < topics.length; j++) {
      const a = topics[i];
      const b = topics[j];
      const shared = [...terms.get(a.id)].filter((w) => terms.get(b.id).has(w) && !tooCommon(w));
      const samePages = [...pages.get(a.id)].filter((u) => pages.get(b.id).has(u));
      if (!shared.length && !samePages.length) continue;
      // Rarer shared words say more; a shared ranking page counts as two words.
      const score = shared.reduce((sum, w) => sum + 1 / df.get(w), 0) + samePages.length * 2;
      const rel = { score, shared: shared.sort((x, y) => df.get(x) - df.get(y)), samePages };
      related.get(a.id).push({ id: b.id, ...rel });
      related.get(b.id).push({ id: a.id, ...rel });
    }
  }
  related.forEach((list, id) => related.set(id, list.sort((x, y) => y.score - x.score).slice(0, RELATED_PER_TOPIC)));
  return related;
}

// "93 articles · authority strong · 1.2K impr" — what the site already has on the topic.
function clusterSub(c) {
  if (!c) return "content cluster";
  return [
    `${fmt(c.size || 0)} articles`,
    c.authority_grade ? `authority ${c.authority_grade}` : null,
    c.gsc_impressions_28d ? `${fmt(c.gsc_impressions_28d)} impr` : null,
  ].filter(Boolean).join(" · ");
}

// Grouped once per load; the panel and the graph both read these.
function indexData({ topics, gscQueries, fanout, clusters }) {
  // Content clusters (CMS articles grouped by topic), biggest first, with article counts.
  const clusterById = new Map(clusters.map((c) => [String(c.cluster_id), c]));
  const clusterLabels = new Map(clusters.map((c) => [String(c.cluster_id), c.label || c.primary_topic]));
  const byTopic = new Map();
  const gscByText = new Map();
  gscQueries.forEach((q) => {
    gscByText.set(key(q.query), q);
    if (!q.query_topic_id) return;
    if (!byTopic.has(q.query_topic_id)) byTopic.set(q.query_topic_id, []);
    byTopic.get(q.query_topic_id).push(q);
  });
  // Most valuable searches first (buyer fit, then commercial value); impressions only break ties.
  byTopic.forEach((qs) => qs.sort((a, b) => (b.buyer_fit ?? -1) - (a.buyer_fit ?? -1)
    || (b.commercial_value ?? -1) - (a.commercial_value ?? -1)
    || (b.impressions || 0) - (a.impressions || 0)));

  const bySeed = new Map();
  fanout.forEach((q) => {
    const k = key(q.seed_topic);
    if (!k) return;
    if (!bySeed.has(k)) bySeed.set(k, { key: k, label: q.seed_topic, queries: [] });
    bySeed.get(k).queries.push(q);
  });

  const topicById = new Map(topics.map((t) => [t.id, t]));
  const related = relateTopics(topics, byTopic);
  // Search topics each cluster answers (its matched cluster), for the panel and the cluster card.
  const topicsByCluster = new Map();
  topics.forEach((t) => {
    if (!t.matched_cluster_id) return;
    const k = String(t.matched_cluster_id);
    topicsByCluster.set(k, (topicsByCluster.get(k) || 0) + 1);
  });
  const articleTotal = clusters.reduce((n, c) => n + (c.size || 0), 0);
  return { topics, gscQueries, byTopic, gscByText, bySeed, topicById, related, clusterLabels, clusters, clusterById, topicsByCluster, articleTotal };
}

// ── Graph builder ─────────────────────────────────────────────────────────────

// Only what's on the map: each pinned topic (GSC queries as rows, impressions as volume) →
// its fan-out seed (synthetic AI-search queries) → the clusters / key pages that answer them.
// A cluster reached from several cards is one shared node, so related topics join up.
function buildGraph(ix, { pinnedTopics, pinnedSeeds, pinnedClusters, showFanout, onAddRelated, bridges, onDismiss, ideas, onRemoveIdea, strategies, onRemoveStrategy }) {
  const nodes = [];
  const edges = [];
  const seen = new Set();
  const add = (n) => {
    if (seen.has(n.id)) return;
    seen.add(n.id);
    nodes.push(n);
  };
  const at = { x: 0, y: 0 };

  const clusterNode = (id) => {
    const nid = `cluster-${id}`;
    add({
      id: nid, type: "plain", position: at,
      data: {
        kind: "cluster", clusterId: String(id), size: ix.clusterById.get(String(id))?.size,
        label: ix.clusterLabels.get(String(id)) || `Cluster ${id}`, sub: clusterSub(ix.clusterById.get(String(id))),
      },
    });
    return nid;
  };

  const fanoutCard = (seed, parentId) => {
    const nid = `fanout-${seed.key}`;
    if (seen.has(nid)) {
      if (parentId) edges.push(edge(parentId, nid, GREY));
      return;
    }
    // A fan-out query someone already searches shows its real Search Console impressions.
    const rows = seed.queries.map((q) => {
      const real = ix.gscByText.get(key(q.query));
      return {
        text: q.query,
        dot: q.cluster_id ? GREEN : RED,
        meta: real ? fmt(real.impressions) : (q.type || "").replace("_", " "),
      };
    });
    const gaps = seed.queries.filter((q) => !q.cluster_id).length;
    add({
      id: nid, type: "card", position: at,
      data: {
        kind: "fanout", label: seed.label, rows,
        sub: `${seed.queries.length} fan-out queries · ${gaps ? `${gaps} gaps` : "all covered"}`,
      },
    });
    if (parentId) edges.push(edge(parentId, nid, gaps === 0 ? GREEN : gaps === seed.queries.length ? RED : AMBER));
    [...new Set(seed.queries.map((q) => q.cluster_id).filter(Boolean))]
      .forEach((cid) => edges.push(edge(nid, clusterNode(cid), GREEN, { dashed: true })));
  };

  ix.topics.forEach((t) => {
    if (!pinnedTopics.has(key(t.topic))) return;
    const qs = ix.byTopic.get(t.id) || [];
    const seed = ix.bySeed.get(key(t.topic));
    const nid = `topic-${t.id}`;
    add({
      id: nid, type: "card", position: at,
      data: {
        kind: "topic", label: t.topic, queryCount: t.queries, hasFanout: !!seed,
        onAddRelated,
        relatedOff: (ix.related.get(t.id) || [])
          .map((r) => ix.topicById.get(r.id)?.topic)
          .filter((label) => label && !pinnedTopics.has(key(label))),
        rows: qs.map((q) => ({
          text: q.query,
          dot: q.on_key_page ? GREEN : q.gap_type ? RED : AMBER,
          meta: fmt(q.impressions),
        })),
        // Value and authority first; impressions are the feedback signal, not the goal.
        sub: [
          t.buyer_fit != null ? `fit ${Number(t.buyer_fit).toFixed(2)}` : null,
          t.matched_cluster_grade ? `authority ${t.matched_cluster_grade}` : t.coverage_status,
          `${fmt(t.impressions)} impr${t.avg_position ? ` · pos ${t.avg_position}` : ""}`,
        ].filter(Boolean).join(" · "),
        tier: t.lead_tier, tierWhy: (t.lead_reasons || []).join(" · "),
        move: topicMove(t), moveGuessed: !t.next_move, moveConfidence: t.next_move_confidence,
        problem: t.problem, impressions: t.impressions,
        // Prefer a query the key page already ranks for: that's the page whose copy gets reworked.
        actionQueryId: (qs.find((q) => q.on_key_page) || qs[0])?.id,
        gapQueries: qs.filter((q) => q.gap_type).map((q) => q.query),
        fanoutGaps: seed ? seed.queries.filter((q) => !q.cluster_id).map((q) => q.query) : [],
      },
    });
    if (t.matched_cluster_id) edges.push(edge(nid, clusterNode(t.matched_cluster_id), GREEN));
    // Pages Google ranks for this topic's searches (plus the key page). Two topics ranking on
    // the same page meet at one page node.
    const byUrl = new Map();
    qs.forEach((q) => {
      const u = normUrl(q.url);
      if (!u) return;
      const cur = byUrl.get(u) || { url: q.url, impressions: 0, queries: 0 };
      cur.impressions += q.impressions || 0;
      cur.queries += 1;
      byUrl.set(u, cur);
    });
    const topPages = [...byUrl.entries()].sort((a, b) => b[1].impressions - a[1].impressions).slice(0, PAGES_PER_TOPIC);
    if (t.matched_key_page && !byUrl.has(normUrl(t.matched_key_page))) {
      topPages.push([normUrl(t.matched_key_page), { url: t.matched_key_page, key: true }]);
    }
    topPages.forEach(([u, pg]) => {
      const pid = `page-${u}`;
      let label = pg.url;
      try { label = new URL(pg.url).pathname || "/"; } catch { /* keep raw */ }
      add({ id: pid, type: "plain", position: at, data: { kind: "page", label, sub: "ranking page", url: pg.url } });
      edges.push({
        ...edge(nid, pid, pg.key ? GREEN : GREY),
        id: `${nid}->${pid}`,
        label: pg.key ? "key page" : `${pg.queries} queries · ${fmt(pg.impressions)}`,
        labelStyle: { fontSize: 9, fill: "#6B7280" },
        labelBgStyle: { fill: "#F9FAFB" },
      });
    });
    if (showFanout && seed) fanoutCard(seed, nid);
  });

  // The owner's own ideas (a framework, a niche angle): no searches yet, there to be connected.
  ideas.forEach((label) => add({
    id: `idea-${key(label)}`, type: "card", position: at,
    data: { kind: "idea", label, rows: [], sub: "Your idea", onRemoveIdea },
  }));

  // Content strategies generated from industry × solution × buyer persona.
  // Each strategy is a parent card with its sub-clusters as child cards.
  strategies.forEach((stg) => {
    const sid = `strategy-${stg.id}`;
    const clusterRows = stg.clusters.map((c) => ({
      text: c.label,
      dot: intentColor(c.intent),
      meta: `${c.queries.length} queries`,
    }));
    add({
      id: sid, type: "card", position: at,
      data: {
        kind: "strategy", label: stg.seed, strategyId: stg.id,
        sub: `Content strategy · ${stg.industry} · ${stg.solution} · ${stg.persona}`,
        rows: clusterRows,
        onRemove: onRemoveStrategy,
      },
    });
    stg.clusters.forEach((c, i) => {
      const cid = `${sid}-cluster-${i}`;
      const queryRows = c.queries.map((q) => ({
        text: q.query,
        dot: intentColor(q.intent || c.intent),
        meta: (q.routing_format || "").replace(/_/g, " ") || (q.intent || "").replace(/_/g, " "),
      }));
      const reason = [
        `Content angle: ${c.label} for ${stg.persona} in ${stg.industry}.`,
        `Answer these searches:\n${c.queries.map((q) => `- ${q.query}`).join("\n")}`,
      ].join("\n\n");
      add({
        id: cid, type: "card", position: at,
        data: {
          kind: "strategyCluster", label: c.label, clusterLabel: c.label,
          sub: `${[stg.solution, (c.intent || "").replace(/_/g, " "), stg.persona].filter(Boolean).join(" · ")}`,
          rows: queryRows,
          reason,
        },
      });
      edges.push(edge(sid, cid, intentColor(c.intent), { dashed: true }));
    });
  });

  // Related topics both on the map: a labelled link, bigger topic → smaller one so dagre
  // reads it left to right. Shared ranking pages already meet at a page node.
  ix.topics.forEach((t) => {
    if (!pinnedTopics.has(key(t.topic))) return;
    (ix.related.get(t.id) || []).forEach((r) => {
      const o = ix.topicById.get(r.id);
      if (!o || !pinnedTopics.has(key(o.topic)) || !r.shared.length) return;
      const [from, to] = (t.impressions || 0) >= (o.impressions || 0) ? [t, o] : [o, t];
      const id = `rel-${from.id}-${to.id}`;
      if (edges.some((e) => e.id === id)) return;
      edges.push({
        ...edge(`topic-${from.id}`, `topic-${to.id}`, "#A78BFA", { dashed: true }),
        id,
        label: r.shared.slice(0, 2).join(", "),
        labelStyle: { fontSize: 10, fill: "#6D28D9", fontWeight: 600 },
        labelBgStyle: { fill: "#F5F3FF" },
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 4,
      });
    });
  });

  // Missing-link suggestions sit between the two cards they were asked for: A → bridge → B.
  // Saved by card label: query_topics ids change on every topic rebuild.
  const cardByLabel = new Map(nodes.filter((n) => n.type === "card").map((n) => [`${n.data.kind}:${key(n.data.label)}`, n.id]));
  bridges.forEach((br) => {
    const a = cardByLabel.get(br.aKey);
    const b = cardByLabel.get(br.bKey);
    if (!a || !b) return;
    const nid = `bridge-${br.id}`;
    const s = br.s || {};
    const between = [br.aLabel, br.bLabel];
    add({
      id: nid, type: "card", position: at,
      data: br.pending
        ? { kind: "bridge", pending: true, label: "Finding the missing link…", sub: `${br.aLabel} ↔ ${br.bLabel}`, rows: [], why: "" }
        : {
          kind: "bridge", bridgeId: br.id, onDismiss, between,
          label: s.title, concept: s.concept, why: s.why, coveredBy: s.covered_by, focus: br.focus,
          sub: [s.concept || "Bridge topic", (s.format || "").replace(/_/g, " "), br.focus].filter(Boolean).join(" · "),
          // The loop: once the bridge page is live, its searches start showing in Search Console.
          rows: (s.queries || []).map((q) => {
            const real = ix.gscByText.get(key(q));
            return real ? { text: q, dot: GREEN, meta: fmt(real.impressions) } : { text: q, dot: VIOLET, meta: "new" };
          }),
          serves: [...(s.serves_a || []), ...(s.serves_b || [])],
        },
    });
    const color = s.covered_by ? GREEN : VIOLET;
    edges.push({ ...edge(a, nid, color, { dashed: true }), animated: !!br.pending });
    edges.push({ ...edge(nid, b, color, { dashed: true }), animated: !!br.pending });
  });

  // Clusters added from the Clusters tab (by label: KMeans renumbers ids on every sync).
  // A pinned topic matched to the same cluster already meets it at the shared node.
  ix.clusters.forEach((c) => {
    if (pinnedClusters.has(key(c.label || c.primary_topic))) clusterNode(c.cluster_id);
  });

  // Seeds added on their own (from a cluster, article or free text) stand alone.
  if (showFanout) {
    pinnedSeeds.forEach((k) => {
      const seed = ix.bySeed.get(k);
      if (seed) fanoutCard(seed, null);
    });
  }

  return { nodes: packLayout(nodes, edges), edges };
}

// ── Left panel ────────────────────────────────────────────────────────────────

const PANEL_TABS = [["topics", "Topics"], ["queries", "Queries"], ["clusters", "Clusters"], ["fanout", "Fan-out"], ["strategy", "Strategy"]];
const DEFAULT_TOPICS = 6;
const ADD_ALL_MAX = 20;

function AddButton({ on, onClick, disabled, title }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title || (on ? "Remove from map" : "Add to map")}
      className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center disabled:opacity-30 ${
        on ? "bg-emerald-600 border-emerald-600 text-white" : "border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-600"
      }`}
    >
      {on ? <Check size={12} /> : <Plus size={12} />}
    </button>
  );
}

function PanelRow({ title, sub, chip, children }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 hover:bg-gray-50">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-900 truncate" title={title}>{title}</p>
        <p className="text-[10px] text-gray-500 truncate">
          {chip}
          {sub}
        </p>
      </div>
      {children}
    </div>
  );
}

function SearchPanel({
  ix, pinnedTopics, pinnedSeeds, pinnedClusters, toggleCluster, toggleTopic, toggleSeed, addTopics, addIdea, focus, setFocus, showFanout, setShowFanout, clear,
  strategyResult, strategyLoading, strategyError, generateStrategy, addStrategy,
}) {
  const [tab, setTab] = useState("topics");
  const [idea, setIdea] = useState("");
  const [q, setQ] = useState("");
  const [moveFilter, setMoveFilter] = useState("");
  const [industry, setIndustry] = useState("");
  const [solution, setSolution] = useState("");
  const [persona, setPersona] = useState("");
  const f = key(q);

  const topics = useMemo(() => ix.topics
    .filter((t) => !moveFilter || MOVES[topicMove(t)]?.action === moveFilter)
    // Backend order: lead tier, then priority — value, not volume.
    .filter((t) => !f || key(t.topic).includes(f) || (ix.byTopic.get(t.id) || []).some((r) => key(r.query).includes(f))),
  [ix, f, moveFilter]);

  const actionCounts = useMemo(() => {
    const c = {};
    ix.topics.forEach((t) => {
      const a = MOVES[topicMove(t)]?.action;
      if (a) c[a] = (c[a] || 0) + 1;
    });
    return c;
  }, [ix]);

  const queries = useMemo(
    () => ix.gscQueries.filter((r) => !f || key(r.query).includes(f)).slice(0, 200),
    [ix, f],
  );
  const clusters = useMemo(
    () => ix.clusters.filter((c) => !f || key(c.label || c.primary_topic).includes(f)),
    [ix, f],
  );
  const seeds = useMemo(
    () => [...ix.bySeed.values()].filter((s) => !f || key(s.label).includes(f) || s.queries.some((r) => key(r.query).includes(f))),
    [ix, f],
  );

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 space-y-2 border-b border-gray-200">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search topics and queries…"
            className="w-full pl-7 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500"
          />
        </div>
        <div className="relative" title="Industry or situation every missing-link suggestion is written for">
          <Target size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-violet-500" />
          <input
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
            placeholder="Focus, e.g. finance, year-end reporting"
            className="w-full pl-7 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-violet-500"
          />
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); addIdea(idea); setIdea(""); }}
          className="flex items-center gap-1.5"
          title="Add your own concept (a framework, a niche angle), then drag it onto a topic"
        >
          <Lightbulb size={13} className="text-yellow-500 shrink-0" />
          <input
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            placeholder="Add an idea, e.g. Minto Pyramid"
            className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-yellow-500"
          />
          <button type="submit" disabled={!idea.trim()} className="text-xs font-medium text-yellow-700 disabled:opacity-40">Add</button>
        </form>
        <div className="flex gap-1">
          {PANEL_TABS.map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-2.5 py-1 text-xs font-medium rounded-full ${tab === id ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "topics" && (
          <div className="flex items-center gap-2">
            <select
              value={moveFilter}
              onChange={(e) => setMoveFilter(e.target.value)}
              className="flex-1 min-w-0 text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-600"
              title="Show only topics whose next move is this action"
            >
              <option value="">All next moves</option>
              {Object.entries(ACTIONS).map(([a, { label }]) => (
                <option key={a} value={a}>{label} ({actionCounts[a] || 0})</option>
              ))}
            </select>
            <button
              onClick={() => addTopics(topics.slice(0, ADD_ALL_MAX).map((t) => key(t.topic)))}
              disabled={!topics.length}
              className="text-xs font-medium text-emerald-700 hover:text-emerald-900 disabled:opacity-40 whitespace-nowrap"
              title={`Add the first ${ADD_ALL_MAX} listed topics to the map`}
            >
              Add {Math.min(topics.length, ADD_ALL_MAX)}
            </button>
          </div>
        )}
        <div className="flex items-center justify-between text-[10px] text-gray-400">
          <span>Click + to add to the map</span>
          <span className="flex items-center gap-3">
            <label className="flex items-center gap-1 cursor-pointer hover:text-gray-700" title="Show AI fan-out cards for topics that have one">
              <input type="checkbox" checked={showFanout} onChange={(e) => setShowFanout(e.target.checked)} className="w-3 h-3" />
              Fan-out
            </label>
            <button onClick={clear} className="hover:text-gray-700">Clear map</button>
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {tab === "topics" && topics.map((t) => {
          const move = MOVES[topicMove(t)];
          return (
            <PanelRow
              key={t.id}
              title={t.topic}
              sub={`${TIERS[t.lead_tier]?.label || "Not scored"}${t.buyer_fit != null ? ` · fit ${Number(t.buyer_fit).toFixed(2)}` : ""} · ${fmt(t.impressions)} impr`}
              chip={move && <span className="font-bold mr-1" style={{ color: move.color }}>{move.label} ·</span>}
            >
              <AddButton on={pinnedTopics.has(key(t.topic))} onClick={() => toggleTopic(key(t.topic))} />
            </PanelRow>
          );
        })}
        {tab === "queries" && queries.map((r) => {
          const t = ix.topicById.get(r.query_topic_id);
          return (
            <PanelRow
              key={r.id || r.query}
              title={r.query}
              sub={`Impressions: ${fmt(r.impressions)}${r.position ? ` · pos ${Number(r.position).toFixed(1)}` : ""}${t ? ` · ${t.topic}` : ""}`}
            >
              <AddButton
                on={!!t && pinnedTopics.has(key(t.topic))}
                disabled={!t}
                title={t ? `Add its topic “${t.topic}” to the map` : "Not in a search topic yet"}
                onClick={() => t && toggleTopic(key(t.topic))}
              />
            </PanelRow>
          );
        })}
        {tab === "clusters" && clusters.length > 0 && (
          <div className="px-3 py-2 text-[10px] text-gray-500 border-b border-gray-100 bg-gray-50">
            <b className="text-gray-800">{fmt(ix.articleTotal)}</b> articles in <b className="text-gray-800">{ix.clusters.length}</b> content clusters
          </div>
        )}
        {tab === "clusters" && clusters.map((c) => {
          const label = c.label || c.primary_topic || `Cluster ${c.cluster_id}`;
          const nTopics = ix.topicsByCluster.get(String(c.cluster_id)) || 0;
          return (
            <PanelRow
              key={c.cluster_id}
              title={label}
              sub={`${clusterSub(c)}${nTopics ? ` · ${nTopics} search topic${nTopics > 1 ? "s" : ""}` : " · no search topic"}`}
            >
              <AddButton on={pinnedClusters.has(key(label))} onClick={() => toggleCluster(key(label))} />
            </PanelRow>
          );
        })}
        {tab === "fanout" && seeds.map((s) => {
          const gaps = s.queries.filter((r) => !r.cluster_id).length;
          return (
            <PanelRow key={s.key} title={s.label} sub={`${s.queries.length} fan-out queries · ${gaps} gaps`}>
              <AddButton on={pinnedSeeds.has(s.key)} onClick={() => toggleSeed(s.key)} />
            </PanelRow>
          );
        })}
        {tab === "strategy" && (
          <div className="p-3 space-y-3">
            <form
              onSubmit={(e) => { e.preventDefault(); generateStrategy(industry, solution, persona); }}
              className="space-y-2"
            >
              <input
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="Industry, e.g. B2B SaaS"
                className="w-full px-2.5 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500"
              />
              <input
                value={solution}
                onChange={(e) => setSolution(e.target.value)}
                placeholder="Solution, e.g. lead generation"
                className="w-full px-2.5 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500"
              />
              <input
                value={persona}
                onChange={(e) => setPersona(e.target.value)}
                placeholder="Buyer persona, e.g. marketing managers"
                className="w-full px-2.5 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500"
              />
              <button
                type="submit"
                disabled={!industry.trim() || !solution.trim() || !persona.trim() || strategyLoading}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-white rounded-lg disabled:opacity-50 transition-opacity"
                style={{ backgroundColor: "#0F766E" }}
              >
                {strategyLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {strategyLoading ? "Generating strategy…" : "Generate content strategy"}
              </button>
            </form>
            {strategyError && (
              <div className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {strategyError}
              </div>
            )}
            {strategyResult && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-teal-800">
                  {strategyResult.seed_topic}
                </div>
                {strategyResult.clusters.map((c, i) => (
                  <div key={i} className="border border-gray-100 rounded-lg p-2.5 bg-white">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate" title={c.label}>{c.label}</p>
                        <p className="text-[10px] text-gray-500">{c.queries.length} queries{c.intent ? ` · ${c.intent.replace(/_/g, " ")}` : ""}</p>
                      </div>
                      <button
                        onClick={() => addStrategy(strategyResult, i)}
                        title={`Add “${c.label}” to the map`}
                        className="shrink-0 w-6 h-6 rounded-full border border-gray-300 text-gray-500 hover:border-teal-500 hover:text-teal-600 flex items-center justify-center"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    <div className="space-y-1">
                      {c.queries.slice(0, 4).map((q, qi) => (
                        <p key={qi} className="text-[10px] text-gray-600 truncate" title={q.query}>{q.query}</p>
                      ))}
                      {c.queries.length > 4 && (
                        <p className="text-[10px] text-gray-400">+{c.queries.length - 4} more</p>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => addStrategy(strategyResult)}
                  className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg"
                >
                  <Plus size={12} /> Add whole strategy to map
                </button>
              </div>
            )}
          </div>
        )}
        {((tab === "topics" && !topics.length) || (tab === "queries" && !queries.length) || (tab === "clusters" && !clusters.length) || (tab === "fanout" && !seeds.length) || (tab === "strategy" && !strategyResult && !strategyLoading)) && (
          <div className="text-xs text-gray-400 p-4 text-center space-y-2">
            {tab === "strategy"
              ? <p>Define industry, solution, and buyer persona to generate a new content direction.</p>
              : <p>None of your Search Console {tab === "fanout" ? "fan-outs" : tab} match.</p>}
            {/* Search only finds what the site already ranks for; an industry or concept goes in as focus or an idea. */}
            {tab !== "strategy" && q.trim() && (
              <div className="flex flex-col gap-1.5 items-center">
                <button onClick={() => setFocus(q.trim())} className="flex items-center gap-1 font-medium text-violet-700 hover:text-violet-900">
                  <Target size={12} /> Use “{q.trim()}” as focus
                </button>
                <button onClick={() => { addIdea(q.trim()); setQ(""); }} className="flex items-center gap-1 font-medium text-yellow-700 hover:text-yellow-900">
                  <Lightbulb size={12} /> Add “{q.trim()}” as an idea card
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Legend ────────────────────────────────────────────────────────────────────

const LEGEND = [
  { type: "topic", label: "Search topic (GSC)" },
  { type: "fanout", label: "Fan-out seed" },
  { type: "cluster", label: "Content cluster" },
  { type: "page", label: "Ranking page" },
  { type: "idea", label: "Your idea" },
  { type: "bridge", label: "Missing link (AI)" },
  { type: "strategy", label: "Content strategy" },
  { type: "strategyCluster", label: "Strategy cluster" },
  { type: "note", label: "Note" },
];
// Row dots the legend can filter on.
const STATUSES = [["answered", GREEN], ["weak", AMBER], ["gap", RED]];
const ROW_KINDS = ["topic", "fanout", "bridge"];

// ── Page ──────────────────────────────────────────────────────────────────────

export default function QueryMapPage() {
  const orgId = useOrgId();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [ix, setIx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  // Search Console first: fan-out (LLM) cards are opt-in.
  const [showFanout, setShowFanout] = useState(false);
  const [layoutKey, setLayoutKey] = useState(0);
  // Content strategy generator: industry × solution × buyer persona.
  const [strategyResult, setStrategyResult] = useState(null);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [strategyError, setStrategyError] = useState(null);
  // Legend filters: hidden node kinds, and row statuses to focus on (empty = all).
  const [legendFilter, setLegendFilter] = usePersistentState(`query-map-filter:${orgId || "default"}`, { kinds: [], statuses: [] });
  const hiddenKinds = legendFilter.kinds;
  const statusDots = useMemo(() => STATUSES.filter(([s]) => legendFilter.statuses.includes(s)).map(([, c]) => c), [legendFilter.statuses]);
  const toggleFilter = (field, v) => setLegendFilter((f) => ({
    ...f, [field]: f[field].includes(v) ? f[field].filter((x) => x !== v) : [...f[field], v],
  }));
  const filterOn = hiddenKinds.length > 0 || statusDots.length > 0;
  // What's on the map, per site. Topics by label: query_topics ids change on every rebuild.
  // null = never set → the top topics by lead potential (the topic view's order).
  const [pinned, setPinned] = usePersistentState(`query-map:${orgId || "default"}`, null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Each source fails alone: no GSC topics still shows fan-outs, and vice versa.
      const [t, q, f, c] = await Promise.all([
        getQueryTopics({ limit: 200 }).catch(() => ({ topics: [] })),
        // The 500 most valuable searches, not the 500 biggest.
        getPerformanceQueries({ limit: 500, sort: "commercial_value", order: "desc" }).catch(() => ({ queries: [] })),
        getFanoutQueries(undefined, 1000).catch(() => ({ queries: [] })),
        getClusters().catch(() => ({ clusters: [] })),
      ]);
      setIx(indexData({ topics: t.topics || [], gscQueries: q.queries || [], fanout: f.queries || [], clusters: c.clusters || [] }));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Each part memoized on its own array, so typing a focus doesn't rebuild the map.
  const pinnedTopicList = pinned?.topics;
  const pinnedSeedList = pinned?.seeds;
  const pinnedTopics = useMemo(() => {
    if (pinnedTopicList) return new Set(pinnedTopicList);
    if (!ix) return new Set();
    return new Set(ix.topics.slice(0, DEFAULT_TOPICS).map((t) => key(t.topic)));
  }, [pinnedTopicList, ix]);
  const pinnedSeeds = useMemo(() => new Set(pinnedSeedList || []), [pinnedSeedList]);
  const pinnedClusterList = pinned?.clusters;
  const pinnedClusters = useMemo(() => new Set(pinnedClusterList || []), [pinnedClusterList]);
  // Missing-link suggestions, saved with the map; `pending` ones are in flight.
  const [pending, setPending] = useState([]);
  const savedBridges = useMemo(() => pinned?.bridges || [], [pinned?.bridges]);
  const ideas = useMemo(() => pinned?.ideas || [], [pinned?.ideas]);
  const focus = pinned?.focus || "";
  const bridges = useMemo(() => [...savedBridges, ...pending], [savedBridges, pending]);

  const save = (topics, seeds) => setPinned((prev) => ({ ...prev, topics: [...topics], seeds: [...seeds] }));
  // Cards call this from inside React Flow; a ref keeps it stable so the graph isn't rebuilt
  // just because the callback changed.
  const pinnedRef = useRef({ pinnedTopics, pinnedSeeds });
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  pinnedRef.current = { pinnedTopics, pinnedSeeds, focus };
  const onAddRelated = useCallback((labels) => {
    const { pinnedTopics: pt, pinnedSeeds: ps } = pinnedRef.current;
    setPinned((prev) => ({ ...prev, topics: [...new Set([...pt, ...labels.map(key)])], seeds: [...ps] }));
  }, [setPinned]);

  // Ideas and focus live with the map; topics/seeds fall back to the defaults when never set.
  const patch = useCallback((fields) => {
    const { pinnedTopics: pt, pinnedSeeds: ps } = pinnedRef.current;
    setPinned((prev) => ({ topics: prev?.topics || [...pt], seeds: prev?.seeds || [...ps], ...prev, ...fields(prev || {}) }));
  }, [setPinned]);
  const addIdea = (label) => {
    const l = label.trim();
    if (l) patch((prev) => ({ ideas: [...new Set([...(prev.ideas || []), l])] }));
  };
  const onRemoveIdea = useCallback((label) => {
    patch((prev) => ({ ideas: (prev.ideas || []).filter((x) => x !== label) }));
  }, [patch]);

  const savedStrategies = useMemo(() => pinned?.strategies || [], [pinned?.strategies]);
  const generateStrategy = useCallback(async (industry, solution, persona) => {
    setStrategyLoading(true);
    setStrategyError(null);
    try {
      const result = await generateContentStrategy(industry, solution, persona, "complex");
      setStrategyResult(result);
    } catch (e) {
      setStrategyError(e?.response?.data?.detail || e.message);
    } finally {
      setStrategyLoading(false);
    }
  }, []);
  const addStrategy = useCallback((result, clusterIndex = null) => {
    // Whole strategy, or just one cluster of it.
    const picked = clusterIndex == null ? result.clusters : [result.clusters[clusterIndex]].filter(Boolean);
    if (!picked.length) return;
    // One strategy per seed: re-adding merges in only the clusters not on the map yet,
    // so adding a cluster then the whole strategy (or the same one twice) never duplicates.
    setPinned((prev) => {
      const list = prev?.strategies || [];
      const existing = list.find((s) => key(s.seed) === key(result.seed_topic));
      if (!existing) {
        const id = `stg-${Date.now().toString(36)}`;
        const stored = { id, industry: result.industry, solution: result.solution, persona: result.persona, seed: result.seed_topic, clusters: picked };
        return { ...prev, strategies: [...list, stored] };
      }
      const have = new Set(existing.clusters.map((c) => key(c.label)));
      const fresh = picked.filter((c) => !have.has(key(c.label)));
      if (!fresh.length) return prev;
      return { ...prev, strategies: list.map((s) => (s === existing ? { ...s, clusters: [...s.clusters, ...fresh] } : s)) };
    });
  }, [setPinned]);
  const onRemoveStrategy = useCallback((id) => {
    setPinned((prev) => ({ ...prev, strategies: (prev?.strategies || []).filter((s) => s.id !== id) }));
  }, [setPinned]);

  // Sticky notes live with the map but outside buildGraph: writing one must not re-layout
  // (and re-frame) the canvas, so the rebuild reads them from a ref.
  const notesRef = useRef(pinned?.notes || []);
  notesRef.current = pinned?.notes || [];
  const flowRef = useRef(null);
  const canvasRef = useRef(null);
  const onNoteChange = useCallback((id, fields) => {
    setPinned((prev) => ({ ...prev, notes: (prev?.notes || []).map((n) => (n.id === id ? { ...n, ...fields } : n)) }));
  }, [setPinned]);
  const onNoteDelete = useCallback((id) => {
    setNodes((ns) => ns.filter((n) => n.id !== `note-${id}`));
    setPinned((prev) => ({ ...prev, notes: (prev?.notes || []).filter((n) => n.id !== id) }));
  }, [setPinned, setNodes]);
  const noteNode = useCallback((n, autoFocus = false) => ({
    id: `note-${n.id}`, type: "note", position: { x: n.x, y: n.y }, dragHandle: ".cursor-move",
    data: { kind: "note", noteId: n.id, text: n.text, autoFocus, onChange: onNoteChange, onDelete: onNoteDelete },
  }), [onNoteChange, onNoteDelete]);
  const addNote = () => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const c = rect && flowRef.current
      ? flowRef.current.screenToFlowPosition({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 })
      : { x: 0, y: 0 };
    const n = { id: Date.now().toString(36), text: "", x: c.x - NOTE_W / 2, y: c.y - NOTE_H / 2 };
    setPinned((prev) => ({ ...prev, notes: [...(prev?.notes || []), n] }));
    setNodes((ns) => [...ns, noteNode(n, true)]);
  };
  const onNodeDragStop = useCallback((_, node) => {
    if (node.type === "note") onNoteChange(node.data.noteId, { x: node.position.x, y: node.position.y });
  }, [onNoteChange]);

  const onDismiss = useCallback((id) => {
    setPinned((prev) => ({ ...prev, bridges: (prev?.bridges || []).filter((b) => b.id !== id) }));
  }, [setPinned]);

  // Dragging one card's handle onto another asks for the topic that connects them.
  const onConnect = useCallback(async ({ source, target }) => {
    const card = (id) => nodesRef.current.find((n) => n.id === id && ["topic", "fanout", "idea", "strategy", "strategyCluster"].includes(n.data.kind));
    const a = card(source);
    const b = card(target);
    if (!a || !b || a.id === b.id) return;
    const { pinnedTopics: pt, pinnedSeeds: ps } = pinnedRef.current;
    const reqId = `${Date.now()}`;
    const angle = pinnedRef.current.focus;
    const base = {
      focus: angle,
      aKey: `${a.data.kind}:${key(a.data.label)}`, bKey: `${b.data.kind}:${key(b.data.label)}`,
      aLabel: a.data.label, bLabel: b.data.label,
    };
    setPending((p) => [...p, { ...base, id: reqId, pending: true }]);
    try {
      const res = await fanoutBridge(
        { label: a.data.label, queries: a.data.rows.map((r) => r.text) },
        { label: b.data.label, queries: b.data.rows.map((r) => r.text) },
        angle,
      );
      const found = (res.suggestions || []).map((sug, i) => ({ ...base, id: `${reqId}-${i}`, s: sug }));
      if (!found.length) alert("The AI answered but suggested nothing usable for these two topics. Try a focus, or another pair.");
      setPinned((prev) => ({
        topics: prev?.topics || [...pt],
        seeds: prev?.seeds || [...ps],
        bridges: [...(prev?.bridges || []), ...found],
      }));
    } catch (e) {
      alert("Missing-link search failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPending((p) => p.filter((x) => x.id !== reqId));
    }
  }, [setPinned]);
  const toggle = (set, k) => {
    const next = new Set(set);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    return next;
  };

  useEffect(() => {
    if (!ix) return;
    const g = buildGraph(ix, { pinnedTopics, pinnedSeeds, pinnedClusters, showFanout, onAddRelated, bridges, onDismiss, ideas, onRemoveIdea, strategies: savedStrategies, onRemoveStrategy });
    setNodes([...g.nodes, ...notesRef.current.map((n) => noteNode(n))]);
    setEdges(g.edges);
    setSelectedId(null);
    setLayoutKey((k) => k + 1); // remount so fitView frames the new layout
  }, [ix, pinnedTopics, pinnedSeeds, pinnedClusters, showFanout, onAddRelated, bridges, onDismiss, ideas, onRemoveIdea, savedStrategies, onRemoveStrategy, noteNode, setNodes, setEdges]);

  const onNodeClick = useCallback((_, node) => {
    if (node.type === "note") return; // a note isn't in the graph: don't dim the map around it
    setSelectedId((id) => (id === node.id ? null : node.id));
  }, []);

  // Dim-on-select, same as the Visual Sitemap: the selected card plus one hop stays lit.
  const connected = useMemo(() => {
    if (!selectedId) return null;
    const ids = new Set([selectedId]);
    edges.forEach((e) => {
      if (e.source === selectedId) ids.add(e.target);
      if (e.target === selectedId) ids.add(e.source);
    });
    return ids;
  }, [edges, selectedId]);

  // Legend filters on top: a hidden kind drops its cards (and their edges); a status filter
  // fades rows without that dot, and fades whole cards that have none.
  const hiddenIds = useMemo(
    () => new Set(nodes.filter((n) => hiddenKinds.includes(n.data.kind)).map((n) => n.id)),
    [nodes, hiddenKinds],
  );
  const displayNodes = useMemo(() => {
    if (!connected && !filterOn) return nodes;
    return nodes.map((n) => {
      let opacity = connected && !connected.has(n.id) && n.type !== "note" ? 0.15 : 1;
      let { data } = n;
      if (statusDots.length && ROW_KINDS.includes(data.kind)) {
        if (!data.rows.some((r) => statusDots.includes(r.dot))) opacity = Math.min(opacity, 0.2);
        data = { ...data, rowFilter: statusDots };
      }
      return { ...n, data, hidden: hiddenIds.has(n.id), selected: n.id === selectedId, style: { ...n.style, opacity } };
    });
  }, [nodes, connected, selectedId, filterOn, statusDots, hiddenIds]);

  const displayEdges = useMemo(() => {
    if (!connected && !hiddenIds.size) return edges;
    return edges.map((e) => ({
      ...e,
      hidden: hiddenIds.has(e.source) || hiddenIds.has(e.target),
      style: connected ? { ...e.style, opacity: e.source === selectedId || e.target === selectedId ? 1 : 0.08 } : e.style,
    }));
  }, [edges, connected, selectedId, hiddenIds]);

  const isEmpty = !loading && ix && ix.topics.length === 0 && ix.bySeed.size === 0 && ix.clusters.length === 0 && !pinned?.strategies?.length && !pinned?.ideas?.length;
  const topicCount = nodes.filter((n) => n.data.kind === "topic").length;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 bg-white shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Network size={20} className="shrink-0" style={{ color: "#018D4C" }} />
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Query Map
            </h1>
            <p className="text-xs text-gray-500 truncate">Topics by lead value and authority, connected by use case · impressions are the feedback, not the goal</p>
          </div>
        </div>
        {/* Title left, stats + Refresh right, like the Visual Sitemap; map settings live in the panel. */}
        <div className="flex items-center gap-3 shrink-0">
          {ix && (
            <div className="hidden md:flex items-center gap-4 text-xs text-gray-500 mr-2 whitespace-nowrap">
              <span><b className="text-gray-800">{topicCount}</b> of {ix.topics.length} topics on map</span>
              {ix.clusters.length > 0 && (
                <span><b className="text-gray-800">{fmt(ix.articleTotal)}</b> articles in {ix.clusters.length} clusters</span>
              )}
              <span><b className="text-gray-800">{ix.bySeed.size}</b> fan-outs</span>
            </div>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
            style={{ background: "#018D4C" }}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-6 py-3 bg-red-50 border-b border-red-200 text-red-700 text-sm shrink-0">
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {loading && (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Loading query map…</div>
      )}

      {isEmpty && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
          <Network size={40} className="opacity-30" />
          <p className="text-sm font-medium">No queries to map yet</p>
          <p className="text-xs text-gray-400">
            Build search topics in{" "}
            <Link to="/dashboard/search-performance?tab=topics" className="underline">Search Performance</Link>{" "}
            or run a <Link to="/dashboard/fanout" className="underline">Query Fan-Out</Link>.
          </p>
        </div>
      )}

      {!loading && !isEmpty && ix && (
        <div className="flex-1 flex flex-row overflow-hidden">
          <div className="bg-white border-r border-gray-200 shrink-0" style={{ width: 300 }}>
            <SearchPanel
              ix={ix}
              pinnedTopics={pinnedTopics}
              pinnedSeeds={pinnedSeeds}
              pinnedClusters={pinnedClusters}
              toggleCluster={(k) => patch(() => ({ clusters: [...toggle(pinnedClusters, k)] }))}
              toggleTopic={(k) => save(toggle(pinnedTopics, k), pinnedSeeds)}
              toggleSeed={(k) => save(pinnedTopics, toggle(pinnedSeeds, k))}
              addTopics={(ks) => save(new Set([...pinnedTopics, ...ks]), pinnedSeeds)}
              addIdea={addIdea}
              focus={focus}
              setFocus={(f) => patch(() => ({ focus: f }))}
              showFanout={showFanout}
              setShowFanout={setShowFanout}
              clear={() => setPinned((prev) => ({ ...prev, topics: [], seeds: [], ideas: [], bridges: [], strategies: [], clusters: [] }))}
              strategyResult={strategyResult}
              strategyLoading={strategyLoading}
              strategyError={strategyError}
              generateStrategy={generateStrategy}
              addStrategy={addStrategy}
            />
          </div>

          <div ref={canvasRef} className="flex-1 relative min-w-0">
            {nodes.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center gap-2 text-gray-400 text-sm">
                <Plus size={28} className="opacity-40" />
                Add topics, queries or fan-outs from the left panel.
              </div>
            ) : (
              <ReactFlow
                key={layoutKey}
                nodes={displayNodes}
                edges={displayEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={NODE_TYPES}
                onNodeClick={onNodeClick}
                onConnect={onConnect}
                onNodeDragStop={onNodeDragStop}
                onInit={(inst) => { flowRef.current = inst; }}
                connectionLineStyle={{ stroke: VIOLET, strokeWidth: 2, strokeDasharray: "5 4" }}
                onPaneClick={() => setSelectedId(null)}
                fitView
                // Never zoom out past readable: a big map scrolls instead of shrinking to dots.
                fitViewOptions={{ padding: 0.1, minZoom: 0.45, maxZoom: 1 }}
                minZoom={0.05}
                maxZoom={2}
                proOptions={{ hideAttribution: true }}
              >
                <Background gap={20} color="#E5E7EB" />
                <Controls />
                <MiniMap
                  nodeColor={(n) => cfg(n.data?.kind).border}
                  maskColor="rgba(255,255,255,0.7)"
                  style={{ bottom: 60 }}
                />
              </ReactFlow>
            )}

            {nodes.length > 0 && (
              <div className="absolute bottom-4 left-4 bg-white border border-gray-200 rounded-lg shadow-sm p-3 flex flex-wrap gap-2 max-w-xs">
                {LEGEND.map(({ type, label }) => {
                  const off = hiddenKinds.includes(type);
                  return (
                    <button
                      key={type}
                      onClick={() => toggleFilter("kinds", type)}
                      title={off ? `Show ${label}` : `Hide ${label}`}
                      className={`flex items-center gap-1.5 text-xs hover:text-gray-900 ${off ? "text-gray-300 line-through" : "text-gray-600"}`}
                    >
                      <div
                        style={{
                          width: 10, height: 10, borderRadius: 3, flexShrink: 0, opacity: off ? 0.35 : 1,
                          background: cfg(type).bg, border: `1.5px solid ${cfg(type).border}`,
                        }}
                      />
                      {label}
                    </button>
                  );
                })}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs w-full mt-1 pt-1 border-t border-gray-100">
                  {STATUSES.map(([s, color]) => {
                    const on = legendFilter.statuses.includes(s);
                    return (
                      <button
                        key={s}
                        onClick={() => toggleFilter("statuses", s)}
                        title={on ? `Stop focusing on ${s} rows` : `Focus on ${s} rows`}
                        className={`flex items-center gap-1 px-1.5 rounded-full ${on ? "bg-gray-100 text-gray-800 font-medium ring-1 ring-gray-300" : "text-gray-400 hover:text-gray-700"}`}
                      >
                        <span className="font-semibold" style={{ color }}>●</span>{s}
                      </button>
                    );
                  })}
                  {filterOn && (
                    <button onClick={() => setLegendFilter({ kinds: [], statuses: [] })} className="ml-auto text-emerald-700 hover:text-emerald-900 font-medium">
                      Show all
                    </button>
                  )}
                  <span className="text-gray-400 w-full">Click to filter · numbers = Search Console impressions</span>
                </div>
              </div>
            )}

            {nodes.length > 0 && (
              <button
                onClick={addNote}
                className="absolute top-3 left-3 z-10 flex items-center gap-1.5 bg-yellow-200 hover:bg-yellow-300 border border-yellow-400 rounded-lg px-3 py-1.5 text-xs font-medium text-yellow-900 shadow-sm"
                title="Drop a sticky note in the middle of the view"
              >
                <StickyNote size={13} /> Add note
              </button>
            )}

            {nodes.length > 0 && !selectedId && (
              <div className="absolute top-3 right-3 bg-white/90 border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-500 shadow-sm">
                Click a card for its next move · drag from a card's right dot to another card to find the missing link
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
